import { isDemoSession } from './session';

/**
 * The third seam: Supabase Storage.
 *
 * Five call sites upload avatars, vehicle photos and driver documents. The
 * alternative was an `isDemoSession()` guard at each, which is how workforce
 * does it — but that spreads demo-awareness through product code, and the
 * count only grows. Patching `client.storage.from` reaches all five from one
 * place and leaves Profile/DriverSignup/DriverProfile untouched.
 *
 * Uploads become object URLs, so a picture the visitor chooses actually
 * appears. They live as long as the tab, which is exactly the demo's lifetime.
 */

const objectUrls = new Map<string, string>();

interface DemoBucket {
  upload: (path: string, file: Blob, opts?: unknown) => Promise<{ data: unknown; error: null }>;
  getPublicUrl: (path: string) => { data: { publicUrl: string } };
  remove: (paths: string[]) => Promise<{ data: unknown; error: null }>;
  download: (path: string) => Promise<{ data: Blob | null; error: null }>;
}

function demoBucket(bucket: string): DemoBucket {
  const key = (path: string) => `${bucket}/${path}`;

  return {
    async upload(path, file) {
      try {
        const url = URL.createObjectURL(file);
        const previous = objectUrls.get(key(path));
        if (previous) URL.revokeObjectURL(previous);
        objectUrls.set(key(path), url);
      } catch {
        /* No object URLs available (jsdom): the path still resolves below. */
      }
      return { data: { path }, error: null };
    },

    getPublicUrl(path) {
      return {
        data: {
          // Falls back to a stable placeholder so a screen that reads a public
          // URL for a file nobody uploaded still renders something.
          publicUrl: objectUrls.get(key(path)) ?? `demo://${bucket}/${path}`,
        },
      };
    },

    async remove(paths) {
      for (const path of paths) {
        const url = objectUrls.get(key(path));
        if (url) URL.revokeObjectURL(url);
        objectUrls.delete(key(path));
      }
      return { data: paths, error: null };
    },

    async download() {
      return { data: null, error: null };
    },
  };
}

export function installDemoStorage(client: unknown): void {
  const target = client as { storage: { from: (bucket: string) => unknown } };
  const realFrom = target.storage.from.bind(target.storage);
  target.storage.from = (bucket: string) =>
    isDemoSession() ? demoBucket(bucket) : realFrom(bucket);
}

/** Test seam: drop any object URLs a session created. */
export function forgetDemoStorage(): void {
  for (const url of objectUrls.values()) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* nothing to revoke */
    }
  }
  objectUrls.clear();
}
